<script setup lang="ts">
import debounce from 'lodash/debounce';
import { message } from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form/interface';
import {
    listConsultProblemType,
    listEnableKfShop,
    saveConsultProblemTypeInfo
} from '@/api/session';
import { useSessionStore } from '@/store/modules/session';

const props = withDefaults(
    defineProps<{
        groupId?: string;
        autoIdentify?: boolean;
        showIdentify?: boolean;
        showSave?: boolean;
        showRelatedShop?: boolean;
        disabled?: boolean;
        disabledShop?: boolean;
        forceRelatedShop?: boolean;
        labelCol?: Record<string, number>;
        wrapperCol?: Record<string, number>;
    }>(),
    {
        groupId: '',
        autoIdentify: false,
        showIdentify: true,
        showSave: true,
        showRelatedShop: true,
        disabled: false,
        disabledShop: false,
        forceRelatedShop: false,
        labelCol: () => ({ span: 7 }),
        wrapperCol: () => ({ span: 17 })
    }
);

const sessionStore = useSessionStore();
const formRef = ref();
const problemLoading = ref(false);
const shopLoading = ref(false);
const identifying = ref(false);
const problemList = ref<SESSION_API.ConsultProblemType[]>([]);
const shopList = ref<SESSION_API.EnableKfShopOption[]>([]);
const formModel = reactive<{
    consultProblemType?: string;
    exchangeInShopId?: string;
    exchangeInShopName: string;
}>({
    consultProblemType: undefined,
    exchangeInShopId: undefined,
    exchangeInShopName: ''
});

const problemOptions = computed(() =>
    problemList.value.map(item => ({
        label: item.desc,
        value: item.code
    }))
);
const shopOptions = computed(() =>
    shopList.value.map(item => ({
        label: item.desc,
        value: item.code
    }))
);
const needRelatedShop = computed(
    () =>
        props.showRelatedShop &&
        (props.forceRelatedShop ||
            problemList.value.find(item => item.code === formModel.consultProblemType)
                ?.relationFlag === 1)
);
const rules = computed<Record<string, Rule[]>>(() => ({
    consultProblemType: [
        {
            required: true,
            message: '请选择咨询问题类型',
            trigger: 'change'
        }
    ],
    exchangeInShopId: [
        {
            validator: async (_rule: unknown, value?: string) => {
                if (needRelatedShop.value && !value) {
                    return Promise.reject('请选择会话相关店铺');
                }
                return Promise.resolve();
            },
            trigger: 'change'
        }
    ]
}));

function normalizeConsultProblemType(value?: string | number) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    return String(value)
        .split(',')
        .map(item => item.trim())
        .find(Boolean);
}

function ensureShopOption(shop?: Partial<SESSION_API.EnableKfShopOption>) {
    if (!shop?.code || !shop.desc) {
        return;
    }
    const option: SESSION_API.EnableKfShopOption = {
        ...shop,
        code: shop.code,
        desc: shop.desc
    };
    if (!shopList.value.some(item => item.code === shop.code)) {
        shopList.value = [option, ...shopList.value];
    }
}

function syncFromSessionBase() {
    const baseInfo = sessionStore.sessionBaseInfo;
    formModel.consultProblemType = normalizeConsultProblemType(baseInfo?.consultProblemType);
    formModel.exchangeInShopId = baseInfo?.exchangeInShopId;
    formModel.exchangeInShopName = baseInfo?.exchangeInShopName ?? '';
    ensureShopOption({
        code: formModel.exchangeInShopId,
        desc: formModel.exchangeInShopName
    });
}

async function fetchProblemList() {
    try {
        problemLoading.value = true;
        problemList.value = await listConsultProblemType();
    } finally {
        problemLoading.value = false;
    }
}

const searchShop = debounce(async (keyword?: string) => {
    try {
        shopLoading.value = true;
        shopList.value = await listEnableKfShop({
            keyword
        });
        ensureShopOption({
            code: formModel.exchangeInShopId,
            desc: formModel.exchangeInShopName
        });
    } finally {
        shopLoading.value = false;
    }
}, 300);

function handleShopChange(value?: string) {
    const option = shopList.value.find(item => item.code === value);
    formModel.exchangeInShopName = option?.desc ?? '';
}

function setSelectedShop(shop?: SESSION_API.EnableKfShopOption) {
    if (!shop?.code || !shop.desc) {
        formModel.exchangeInShopId = undefined;
        formModel.exchangeInShopName = '';
        return;
    }
    ensureShopOption(shop);
    formModel.exchangeInShopId = shop.code;
    formModel.exchangeInShopName = shop.desc;
    formRef.value?.validateFields?.(['exchangeInShopId']);
}

async function identifyShop(options?: { silent?: boolean; overwrite?: boolean }) {
    try {
        identifying.value = true;
        const shop = await sessionStore.getDefaultRelatedShop(
            props.groupId || sessionStore.groupID
        );
        if (!shop) {
            if (!options?.silent) {
                message.warning('未识别到会话相关店铺');
            }
            return;
        }
        if (formModel.exchangeInShopId && options?.overwrite === false) {
            return;
        }
        ensureShopOption(shop);
        formModel.exchangeInShopId = shop.code;
        formModel.exchangeInShopName = shop.desc;
        await nextTick();
        formRef.value?.validateFields?.(['exchangeInShopId']);
        if (!options?.silent) {
            message.success('已识别店铺');
        }
    } finally {
        identifying.value = false;
    }
}

async function validate() {
    await formRef.value?.validate();
    return getPayload();
}

function getPayload(): SESSION_API.SaveConsultProblemTypeInfoReq {
    return {
        groupId: props.groupId || sessionStore.groupID || '',
        consultProblemType: formModel.consultProblemType,
        exchangeInShopId: needRelatedShop.value ? formModel.exchangeInShopId : undefined
    };
}

function getSelectedShop() {
    if (!formModel.exchangeInShopId) {
        return undefined;
    }
    return {
        code: formModel.exchangeInShopId,
        desc: formModel.exchangeInShopName
    };
}

async function save() {
    const payload = await validate();
    await saveConsultProblemTypeInfo(payload);
    await sessionStore.refreshSessionBaseInfo(payload.groupId);
    message.success('保存成功');
}

onMounted(async () => {
    syncFromSessionBase();
    await fetchProblemList();
    if (props.autoIdentify) {
        await identifyShop({
            silent: true,
            overwrite: false
        });
    }
});

watch(
    () => sessionStore.sessionBaseInfo,
    () => {
        syncFromSessionBase();
    }
);

defineExpose({
    validate,
    getPayload,
    getSelectedShop,
    setSelectedShop,
    identifyShop,
    needRelatedShop
});
</script>

<template>
    <a-form
        ref="formRef"
        :model="formModel"
        :rules="rules"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        class="consult-info-form"
    >
        <a-form-item
            label="咨询问题类型"
            name="consultProblemType"
        >
            <a-select
                v-model:value="formModel.consultProblemType"
                :loading="problemLoading"
                :options="problemOptions"
                :disabled="disabled"
                placeholder="请选择咨询问题类型"
                allow-clear
            />
        </a-form-item>
        <a-form-item
            v-if="needRelatedShop"
            label="会话相关店铺"
            name="exchangeInShopId"
        >
            <div class="flex gap-8px">
                <a-select
                    v-model:value="formModel.exchangeInShopId"
                    class="flex-1"
                    show-search
                    :filter-option="false"
                    :loading="shopLoading"
                    :options="shopOptions"
                    :disabled="disabled || disabledShop"
                    placeholder="请输入店铺ID或店铺名称"
                    allow-clear
                    @focus="searchShop('')"
                    @search="searchShop"
                    @change="handleShopChange"
                />
                <a-button
                    v-if="showIdentify && !disabledShop"
                    type="primary"
                    :loading="identifying"
                    :disabled="disabled"
                    @click="identifyShop({ overwrite: true })"
                >
                    识别店铺
                </a-button>
            </div>
        </a-form-item>
        <a-form-item
            v-if="showSave"
            :wrapper-col="{ offset: labelCol.span, span: wrapperCol.span }"
        >
            <a-button
                type="primary"
                :disabled="disabled"
                @click="save"
            >
                保存
            </a-button>
        </a-form-item>
    </a-form>
</template>
